import { useFormik } from "formik";
import * as Yup from "yup";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import Select from "../../../components/select";
import Button from "../../../components/button";
import { useDispatch, useSelector } from "react-redux";
import { createBackup } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PaymentModal from "../../../components/paymentModal";

const CreateVolumeBackup = () => {
  document.title = "Create Volume Backup - CloudVantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const projectId = useSelector((state) => state.user.selectedProject)?.id;
  const currency = useSelector((state) => state.user.currency);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === "USD"
        ? Number(state.user.virtualAccount.dollar_balance) || 0
        : Number(state.user.virtualAccount.balance) || 0
      : 0
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);
  const selectedVolume = JSON.parse(sessionStorage.getItem("selectedVolume"));

  const parsedVolumeSize = selectedVolume?.volumeSize
    ? typeof selectedVolume?.volumeSize === "number"
      ? selectedVolume?.volumeSize
      : parseFloat(selectedVolume?.volumeSize?.replace(/[^0-9.]/g, ""))
    : 0;

  const volumePaymentOptions = [
    { value: "", label: "Select an option" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const calculateEstimatedCost = (volumeSize, currency, paymentMethod) => {
    if (!volumeSize || !paymentMethod) return { formatted: "0.00", numeric: 0 };

    const volumeSizePrice = currency === "NGN" ? 270 : 0.12;
    const volumeSizeValue = parseFloat(volumeSize);

    let totalCost = volumeSizeValue * volumeSizePrice;

    if (paymentMethod === "yearly") {
      const yearlyPrice = totalCost * 12;
      const discount = yearlyPrice * 0.01;
      totalCost = yearlyPrice - discount;
    }

    return {
      formatted: totalCost.toLocaleString("en-US", {
        style: "currency",
        currency: currency === "NGN" ? "NGN" : "USD",
      }),
      numeric: totalCost,
    };
  };

  const [calculatedEstimate, setCalculatedEstimate] = useState("0.00");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const createBackupForm = useFormik({
    initialValues: {
      snapshotName: "",
      payment: "",
      description: "",
    },
    validationSchema: Yup.object({
      snapshotName: Yup.string().required("Backup name cannot be empty"),
      payment: Yup.string().required("Payment method is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { snapshotName, payment, description } = values;

      let snapshotData = {
        name: snapshotName,
        volumeId: selectedVolume?.volumeId,
        payment_method: payment,
        description,
        projectId,
      };

      const { payload } = await dispatch(createBackup({ snapshotData, navigate }));
      if (payload?.statuscode === "00" || payload?.statuscode === 200) {
        toast.success("Volume backup created successfully!", { theme: "colored" });
        navigate("/manageVolumeBackups");
        resetForm();
      } else {
        const errorMsg = payload?.statusmessage || payload?.message || "Failed to create volume backup";
        toast.error(errorMsg, { theme: "colored" });
      }
    },
  });

  useEffect(() => {
    const estimate = calculateEstimatedCost(
      parsedVolumeSize,
      currency,
      createBackupForm.values.payment
    );
    setCalculatedEstimate(estimate.formatted);
  }, [parsedVolumeSize, currency, createBackupForm.values.payment]);

  const handleCreateBackupClick = (e) => {
    e.preventDefault();
    // Students are not billed, always proceed to create
    createBackupForm.handleSubmit();
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue mb-6">
        Create Volume Backup for {selectedVolume?.volumeName}
      </p>

      <div className="grid gap-4">
        <form className="gap-y-6 grid">
          <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
            <Input
              type={"text"}
              label="Volume Backup Name"
              name={"snapshotName"}
              onBlur={createBackupForm.handleBlur}
              onChange={createBackupForm.handleChange}
              placeholder={"Give your backup a name"}
              value={createBackupForm.values.snapshotName}
              onError={
                createBackupForm.errors.snapshotName &&
                createBackupForm.touched.snapshotName
                  ? createBackupForm.errors.snapshotName
                  : null
              }
            />
            <Input
              type={"text"}
              label="Size"
              name={"size"}
              value={`${parsedVolumeSize} GB`}
              disabled={true}
            />
            <Select
              label="Payment:"
              name="payment"
              options={volumePaymentOptions}
              valueKey={"value"}
              labelKey={"label"}
              selectValue={createBackupForm.values.payment}
              selectBlur={createBackupForm.handleBlur}
              onChange={(event) => createBackupForm.setFieldValue("payment", event.target.value)}
              onError={
                createBackupForm.touched.payment && createBackupForm.errors.payment
                  ? createBackupForm.errors.payment
                  : null
              }
            />
            <TextArea
              label="Description"
              name="description"
              value={createBackupForm.values.description}
              onBlur={createBackupForm.handleBlur}
              onChange={createBackupForm.handleChange}
              onError={
                createBackupForm.errors.description &&
                createBackupForm.touched.description
                  ? createBackupForm.errors.description
                  : null
              }
            />
            <div className="flex justify-between items-center gap-2">
              <div>
                <div className="text-xs">Estimated cost:</div>
                <div className="font-semibold crimson-font text-primaryBlue text-lg">
                  {calculatedEstimate}
                </div>
              </div>
              <Button
                buttonRole="submit"
                buttonText="Create Volume Backup"
                handleClick={(e) => handleCreateBackupClick(e)}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={calculateEstimatedCost(
          parsedVolumeSize,
          currency,
          createBackupForm.values.payment
        ).numeric}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createBackupForm.handleSubmit()}
      />
    </div>
  );
};

export default CreateVolumeBackup;